import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { I18nService } from '../core/i18n.service';

@Component({
  selector: 'app-hall-of-fame-page',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section class="page">
      <header class="hero">
        <p class="eyebrow">ECL VAULT</p>
        <h1>{{ t('hof.title') }}</h1>
        <p class="subtitle">{{ t('hof.subtitle') }}</p>
      </header>

      <section class="tables">
        <article class="card panel">
          <header class="panel-head">
            <h2>{{ t('hof.topTeams') }}</h2>
            <span class="chip">{{ t('hof.mock') }}</span>
          </header>
          <div class="table">
            <div class="row row--head">
              <span>{{ t('hof.col.rank') }}</span>
              <span>{{ t('hof.col.team') }}</span>
              <span>{{ t('hof.col.points') }}</span>
              <span>{{ t('hof.col.titles') }}</span>
            </div>
            @for (team of topTeams; track team.rank) {
              <div class="row">
                <span class="rank">{{ team.rank }}</span>
                <span class="name">{{ team.name }}</span>
                <span class="metric">{{ team.points }}</span>
                <span class="metric">{{ team.titles }}</span>
              </div>
            }
          </div>
        </article>

        <article class="card panel">
          <header class="panel-head">
            <h2>{{ t('hof.topPlayers') }}</h2>
            <span class="chip">{{ t('hof.mock') }}</span>
          </header>
          <div class="table">
            <div class="row row--head">
              <span>{{ t('hof.col.rank') }}</span>
              <span>{{ t('hof.col.player') }}</span>
              <span>{{ t('hof.col.team') }}</span>
              <span>{{ t('hof.col.rating') }}</span>
            </div>
            @for (player of topPlayers; track player.rank) {
              <div class="row">
                <span class="rank">{{ player.rank }}</span>
                <span class="name">{{ player.name }}</span>
                <span>{{ player.team }}</span>
                <span class="metric">{{ player.rating }}</span>
              </div>
            }
          </div>
        </article>
      </section>

      <section class="card panel panel--wide">
        <header class="panel-head">
          <h2>{{ t('hof.recentWinners') }}</h2>
          <span class="chip chip--accent">{{ t('hof.feed') }}</span>
        </header>
        <div class="winners">
          @for (winner of recentWinners; track winner.id) {
            <article class="winner-card">
              <p class="winner-season">{{ winner.season }}</p>
              <h3>{{ winner.title }}</h3>
              <p class="winner-team">{{ winner.team }}</p>
              <p class="winner-meta">{{ winner.mode }} | {{ winner.dateLabel }}</p>
            </article>
          }
        </div>
      </section>

      <section class="card note">
        <p>{{ t('hof.note') }}</p>
      </section>
    </section>
  `,
  styles: `
    .page { padding: 1.2rem; max-width: 1180px; margin: 0 auto; display: grid; gap: 0.9rem; }
    .hero { border: 1px solid rgba(111, 164, 255, 0.3); border-radius: 16px; background: linear-gradient(160deg, rgba(26, 42, 71, 0.96) 0%, rgba(18, 30, 50, 0.96) 60%, rgba(26, 29, 55, 0.96) 100%); padding: 1.2rem; box-shadow: var(--shadow-soft); }
    .eyebrow { margin: 0 0 0.25rem; color: #c8d8f2; font-size: 0.74rem; letter-spacing: 0.13em; font-weight: 700; text-transform: uppercase; }
    .hero h1 { margin: 0 0 0.35rem; color: var(--text-strong); font-size: 1.7rem; line-height: 1.1; }
    .subtitle { margin: 0; color: var(--text-muted); max-width: 700px; }
    .card { border: 1px solid var(--line-soft); border-radius: 14px; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); padding: 1rem; box-shadow: var(--shadow-soft); }
    .tables { display: grid; grid-template-columns: repeat(auto-fit, minmax(360px, 1fr)); gap: 0.9rem; }
    .panel { overflow: hidden; }
    .panel-head { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; margin-bottom: 0.65rem; }
    h2 { margin: 0; color: var(--text-strong); font-size: 1.15rem; letter-spacing: 0.01em; }
    .chip { display: inline-block; border-radius: 999px; border: 1px solid rgba(130, 170, 238, 0.4); background: rgba(106, 162, 255, 0.16); color: #d9e8ff; font-size: 0.72rem; font-weight: 700; padding: 0.15rem 0.45rem; text-transform: uppercase; letter-spacing: 0.06em; }
    .chip--accent { border-color: rgba(183, 148, 255, 0.45); background: rgba(176, 120, 255, 0.18); color: #e8d9ff; }
    .table { border: 1px solid rgba(71, 97, 141, 0.45); border-radius: 10px; overflow: hidden; background: rgba(12, 19, 31, 0.6); }
    .row { display: grid; grid-template-columns: 42px 1fr 92px 80px; gap: 0.4rem; align-items: center; padding: 0.5rem 0.65rem; border-bottom: 1px solid rgba(65, 86, 121, 0.35); color: var(--text-muted); font-size: 0.9rem; }
    .row:last-child { border-bottom: 0; }
    .row--head { background: rgba(94, 132, 197, 0.2); color: #d9e7ff; font-weight: 700; font-size: 0.78rem; letter-spacing: 0.04em; text-transform: uppercase; }
    .rank { color: #f4f8ff; font-weight: 700; }
    .name { color: var(--text-strong); font-weight: 600; }
    .metric { color: #d7e5ff; font-weight: 700; text-align: right; }
    .winners { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 0.7rem; }
    .winner-card { border: 1px solid rgba(83, 113, 163, 0.45); border-radius: 11px; background: linear-gradient(180deg, rgba(15, 24, 40, 0.82) 0%, rgba(13, 21, 35, 0.82) 100%); padding: 0.75rem; }
    .winner-season { margin: 0 0 0.25rem; color: #cbd9f0; font-size: 0.76rem; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700; }
    h3 { margin: 0 0 0.3rem; color: #e5efff; font-size: 1.02rem; }
    .winner-team { margin: 0 0 0.24rem; color: #b5d0ff; font-weight: 700; }
    .winner-meta { margin: 0; color: var(--text-muted); font-size: 0.86rem; }
    .note { border-color: rgba(178, 145, 255, 0.35); background: linear-gradient(180deg, rgba(37, 27, 59, 0.72) 0%, rgba(21, 20, 44, 0.72) 100%); }
    .note p { margin: 0; color: #e0d0ff; }
    @media (max-width: 740px) {
      .tables { grid-template-columns: 1fr; }
      .row { grid-template-columns: 34px 1fr 74px 64px; font-size: 0.84rem; padding: 0.46rem 0.55rem; }
      .hero h1 { font-size: 1.45rem; }
    }
  `
})
export class HallOfFamePageComponent {
  private readonly i18n = inject(I18nService);

  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }

  protected readonly topTeams = [
    { rank: 1, name: 'Nova Six', points: 1240, titles: 3 },
    { rank: 2, name: 'Atlas Core', points: 1135, titles: 2 },
    { rank: 3, name: 'Phantom Unit', points: 1050, titles: 1 },
    { rank: 4, name: 'Crimson Echo', points: 980, titles: 1 },
    { rank: 5, name: 'Astra Wolves', points: 930, titles: 0 }
  ];

  protected readonly topPlayers = [
    { rank: 1, name: 'Raven', team: 'Nova Six', rating: '1.34' },
    { rank: 2, name: 'Skorn', team: 'Atlas Core', rating: '1.27' },
    { rank: 3, name: 'Lyra', team: 'Phantom Unit', rating: '1.22' },
    { rank: 4, name: 'Mako', team: 'Crimson Echo', rating: '1.19' },
    { rank: 5, name: 'Nyx', team: 'Astra Wolves', rating: '1.15' }
  ];

  protected readonly recentWinners = [
    { id: 1, season: 'Spring Split', title: 'ECL Main Cup', team: 'Nova Six', mode: '4v4', dateLabel: 'March 2026' },
    { id: 2, season: 'Winter Event', title: 'ECL Night Clash', team: 'Atlas Core', mode: '3v3', dateLabel: 'February 2026' },
    { id: 3, season: 'Open Series', title: 'ECL Challenger', team: 'Phantom Unit', mode: '2v2', dateLabel: 'January 2026' }
  ];
}
